import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import AdminMobileShell from "./AdminMobileShell";
import {
  Summary, SearchBar, Chips, EmptyState, Fab, Sheet, ActionRow, DetailRow,
  Field, TextInput, TextAreaInput, SelectInput, Pill, Mono, Ico, fmt,
} from "../produits/productMobileKit";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";
import {
  deleteCurrency, deletePaymentMode, deleteTax,
  listCurrencies, listPaymentModes, listTaxes,
  saveCurrency, savePaymentMode, saveTax, toggleTax,
} from "./adminMobileApi";

const PAGE = 20;
const numberValue = (value) => {
  const parsed = Number(String(value ?? "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};
const errorMessage = (error, fallback) => error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

const CONFIG = {
  taxes: {
    title: "Taxes", singular: "taxe", permission: "Admin.Taxes",
    empty: () => ({ code: "", name: "", rate: "0", status: "active", parent_id: "", effective_date: "", description: "" }),
    load: listTaxes, save: saveTax, remove: deleteTax,
  },
  currencies: {
    title: "Devises", singular: "devise", permission: "Admin.Devises",
    empty: () => ({ name: "", code: "", symbol: "", position: "before" }),
    load: listCurrencies, save: saveCurrency, remove: deleteCurrency,
  },
  paymentModes: {
    title: "Modes de paiement", singular: "mode de paiement", permission: "Admin.ModesPaiement",
    empty: () => ({ name: "", mode_type: "cash", credentials: "" }),
    load: listPaymentModes, save: savePaymentMode, remove: deletePaymentMode,
  },
};

const TYPE_OPTIONS = [
  { value: "cash", label: "Espèces" }, { value: "bank", label: "Virement bancaire" },
  { value: "card", label: "Carte" }, { value: "cheque", label: "Chèque" },
  { value: "mobile", label: "Mobile Money" }, { value: "other", label: "Autre" },
];

export default function AdminSimpleResourceMobile({ resource }) {
  const cfg = CONFIG[resource];
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [detail, setDetail] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(cfg.empty);
  const [saving, setSaving] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const inFlight = useRef(false);

  const canView = user?.is_superadmin || hasPermission(`${cfg.permission}.view`);
  const canCreate = user?.is_superadmin || hasPermission(`${cfg.permission}.create`);
  const canEdit = user?.is_superadmin || hasPermission(`${cfg.permission}.edit`);
  const canDelete = user?.is_superadmin || hasPermission(`${cfg.permission}.delete`);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  const load = useCallback(async (nextPage = 1, append = false) => {
    if (!selectedCompany || !canView) return;
    setLoading(true);
    try {
      const result = await cfg.load({
        companyId: selectedCompany, page: nextPage, limit: PAGE,
        search: debouncedQuery, filterCompany: true,
      });
      setRows((prev) => append ? [...prev, ...result.rows] : result.rows);
      setTotal(result.total || result.rows.length);
      setPage(nextPage);
    } catch (error) {
      message.error(errorMessage(error, `Impossible de charger les ${cfg.title.toLowerCase()}`));
      if (!append) setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView, cfg, debouncedQuery, selectedCompany]);

  useEffect(() => { load(1, false); }, [load]);

  const visible = useMemo(() => rows.filter((row) => {
    if (resource !== "taxes" || status === "all") return true;
    return row.status === status;
  }), [resource, rows, status]);

  const openForm = (row = null) => {
    if (resource === "currencies" && row && !row.is_deletable) {
      message.warning("Cette devise système ne peut pas être modifiée");
      return;
    }
    setEditing(row);
    if (!row) setForm(cfg.empty());
    else if (resource === "taxes") setForm({
      code: row.code || "", name: row.name || "", rate: String(row.rate ?? 0), status: row.status || "active",
      parent_id: String(row.parent_id || ""), effective_date: row.effective_date ? String(row.effective_date).slice(0, 10) : "", description: row.description || "",
    });
    else if (resource === "currencies") setForm({ name: row.name || "", code: row.code || "", symbol: row.symbol || "", position: row.position || "before" });
    else setForm({ name: row.name || "", mode_type: row.mode_type || "cash", credentials: row.credentials || "" });
    setFormOpen(true);
    setDetail(null);
  };

  const submit = async () => {
    if (inFlight.current || !form.name?.trim()) return;
    if ((resource === "taxes" || resource === "currencies") && !form.code?.trim()) return;
    inFlight.current = true;
    setSaving(true);
    try {
      let payload;
      if (resource === "taxes") payload = {
        company_id: editing?.company_id || selectedCompany,
        code: form.code.trim(), name: form.name.trim(), rate: numberValue(form.rate), status: form.status,
        parent_id: form.parent_id || undefined, effective_date: form.effective_date || undefined,
        description: form.description.trim() || null,
      };
      else if (resource === "currencies") payload = {
        company_id: editing?.company_id || selectedCompany,
        name: form.name.trim(), code: form.code.trim().toUpperCase(), symbol: form.symbol.trim(), position: form.position,
      };
      else payload = {
        company_id: editing?.company_id || selectedCompany,
        name: form.name.trim(), mode_type: form.mode_type, credentials: form.credentials.trim() || undefined,
      };
      await cfg.save(editing?.id, payload);
      message.success(editing ? "Enregistrement mis à jour" : "Enregistrement créé");
      setFormOpen(false);
      setEditing(null);
      await load(1, false);
    } catch (error) {
      message.error(errorMessage(error, `Impossible d'enregistrer ce ${cfg.singular}`));
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  const remove = (row) => {
    if (resource === "currencies" && !row.is_deletable) return;
    Modal.confirm({
      title: `Supprimer ce ${cfg.singular} ?`, content: row.name, okText: "Supprimer", okType: "danger", cancelText: "Annuler", centered: true,
      onOk: async () => {
        try { await cfg.remove(row.id); message.success("Suppression effectuée"); setDetail(null); load(1, false); }
        catch (error) { message.error(errorMessage(error, "Suppression impossible")); }
      },
    });
  };

  const changeTaxStatus = async (row) => {
    const next = row.status === "active" ? "inactive" : "active";
    try { await toggleTax(row.id, next); message.success("Statut mis à jour"); setDetail(null); load(1, false); }
    catch (error) { message.error(errorMessage(error, "Mise à jour impossible")); }
  };

  if (!canView) return <AdminMobileShell title={cfg.title}><div className="body"><EmptyState icon="alert-circle" title="Accès non autorisé" /></div></AdminMobileShell>;
  if (!selectedCompany) return <AdminMobileShell title={cfg.title}><div className="body"><EmptyState icon="store" title="Entreprise requise" text="Sélectionnez une entreprise." /></div></AdminMobileShell>;

  const activeCount = resource === "taxes" ? rows.filter((row) => row.status === "active").length : rows.length;

  return (
    <AdminMobileShell title={cfg.title} subtitle={`${fmt(total)} élément${total > 1 ? "s" : ""}`}>
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <Summary items={[{ label: "Total", value: fmt(total) }, { label: resource === "taxes" ? "Actives" : "Chargés", value: fmt(activeCount) }]} />
        <SearchBar value={query} onChange={setQuery} placeholder={`Rechercher un ${cfg.singular}…`} />
        {resource === "taxes" ? <Chips value={status} onChange={setStatus} options={[{ key: "all", label: "Toutes" }, { key: "active", label: "Actives" }, { key: "inactive", label: "Inactives" }]} /> : null}
      </div>
      <div className="body">
        {loading && !rows.length ? <EmptyState loading title="Chargement…" /> : !visible.length ? <EmptyState icon="tag" title={`Aucun ${cfg.singular}`} /> : (
          <div className="list">
            {visible.map((row) => {
              const secondary = resource === "taxes" ? `${row.code} · ${fmt(row.rate)} %` : resource === "currencies" ? `${row.code} · ${row.symbol}` : TYPE_OPTIONS.find((item) => item.value === row.mode_type)?.label || row.mode_type;
              return <button key={row.id} className="rcard" onClick={() => setDetail(row)}>
                <Mono name={row.code || row.name} square />
                <div className="rcard__main"><div className="rcard__name">{row.name}</div><div className="rcard__meta">{resource === "taxes" ? <Pill tone={row.status === "active" ? "ok" : "off"} label={row.status === "active" ? "Active" : "Inactive"} /> : null}<span className="rcard__sub">{secondary}</span></div></div>
                <span className="rcard__chev"><Ico name="chevron-right" /></span>
              </button>;
            })}
            {rows.length < total ? <button className="btn btn--ghost" onClick={() => load(page + 1, true)} disabled={loading}>{loading ? "Chargement…" : `Charger plus (${rows.length}/${total})`}</button> : null}
          </div>
        )}
      </div>
      {canCreate ? <Fab label={`Nouveau ${cfg.singular}`} onClick={() => openForm()} /> : null}

      <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.name}>
        {detail ? <>
          {resource !== "paymentModes" ? <DetailRow label="Code" value={detail.code || "—"} /> : null}
          {resource === "taxes" ? <><DetailRow label="Taux" value={`${fmt(detail.rate)} %`} /><DetailRow label="Statut" value={detail.status === "active" ? "Active" : "Inactive"} /><DetailRow label="Date d'effet" value={detail.effective_date || "—"} /><DetailRow label="Description" value={detail.description || "—"} /></> : null}
          {resource === "currencies" ? <><DetailRow label="Symbole" value={detail.symbol || "—"} /><DetailRow label="Position" value={detail.position === "after" ? "Après le montant" : "Avant le montant"} /></> : null}
          {resource === "paymentModes" ? <><DetailRow label="Type" value={TYPE_OPTIONS.find((item) => item.value === detail.mode_type)?.label || detail.mode_type} /><DetailRow label="Entreprise" value={detail.company_name || (detail.company_id ? `#${detail.company_id}` : "Global")} /><DetailRow label="Informations" value={detail.credentials || "—"} /></> : null}
          {canEdit && !(resource === "currencies" && !detail.is_deletable) ? <ActionRow icon="pencil" label="Modifier" onClick={() => openForm(detail)} /> : null}
          {resource === "taxes" && canEdit ? <ActionRow icon="rotate-ccw" label={detail.status === "active" ? "Désactiver" : "Activer"} onClick={() => changeTaxStatus(detail)} /> : null}
          {canDelete && !(resource === "currencies" && !detail.is_deletable) ? <ActionRow icon="trash-2" label="Supprimer" danger onClick={() => remove(detail)} /> : null}
        </> : null}
      </Sheet>

      <Sheet open={formOpen} onClose={() => setFormOpen(false)} title={editing ? `Modifier le ${cfg.singular}` : `Nouveau ${cfg.singular}`}>
        {resource !== "paymentModes" ? <Field label="Code *"><TextInput value={form.code} onChange={(code) => setForm((f) => ({ ...f, code: resource === "currencies" ? code.toUpperCase().slice(0, 3) : code }))} /></Field> : null}
        <Field label="Nom *"><TextInput value={form.name} onChange={(name) => setForm((f) => ({ ...f, name }))} autoFocus /></Field>
        {resource === "taxes" ? <>
          <Field label="Taux (%) *"><TextInput value={form.rate} onChange={(rate) => setForm((f) => ({ ...f, rate }))} inputMode="decimal" /></Field>
          <Field label="Taxe parente"><SelectInput value={form.parent_id} onChange={(parent_id) => setForm((f) => ({ ...f, parent_id: parent_id || "" }))} options={rows.filter((row) => row.id !== editing?.id).map((row) => ({ value: String(row.id), label: `${row.code} · ${row.name}` }))} placeholder="Aucune" /></Field>
          <Field label="Statut"><SelectInput value={form.status} onChange={(statusValue) => setForm((f) => ({ ...f, status: statusValue }))} options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} /></Field>
          <Field label="Date d'effet"><div className="ctrl"><input type="date" value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} /></div></Field>
          <Field label="Description"><TextAreaInput value={form.description} onChange={(description) => setForm((f) => ({ ...f, description }))} rows={3} /></Field>
        </> : null}
        {resource === "currencies" ? <>
          <Field label="Symbole *"><TextInput value={form.symbol} onChange={(symbol) => setForm((f) => ({ ...f, symbol }))} /></Field>
          <Field label="Position"><SelectInput value={form.position} onChange={(position) => setForm((f) => ({ ...f, position }))} options={[{ value: "before", label: "Avant le montant" }, { value: "after", label: "Après le montant" }]} /></Field>
        </> : null}
        {resource === "paymentModes" ? <>
          <Field label="Type *"><SelectInput value={form.mode_type} onChange={(mode_type) => setForm((f) => ({ ...f, mode_type }))} options={TYPE_OPTIONS} /></Field>
          <Field label="Informations"><TextAreaInput value={form.credentials} onChange={(credentials) => setForm((f) => ({ ...f, credentials }))} rows={3} /></Field>
        </> : null}
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setFormOpen(false)}>Annuler</button><button className="btn btn--primary" onClick={submit} disabled={saving || !form.name?.trim() || ((resource === "taxes" || resource === "currencies") && !form.code?.trim())}>{saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer"}</button></div>
      </Sheet>
    </AdminMobileShell>
  );
}
